/**
 * Display formatters for the Campaign Summary dashboard.
 *
 * Kept apart from the components so the interval pivot, the agent table and
 * the KPI tiles cannot render the same metric three different ways — the
 * dashboard's whole value is that "AHT" reads identically wherever it appears.
 */
import type { SummaryMetrics, IntervalRow, PivotRow } from './types.js'

/**
 * H:MM:SS, dropping the hour segment below an hour.
 *
 * Negative input clamps to zero rather than rendering "-0:-5": durations here
 * are derived from `now - startedAt`, and a client whose clock runs behind the
 * server would otherwise show negative talk times on freshly-started calls.
 */
export function formatDuration(totalSec: number): string {
  const safe = Number.isFinite(totalSec) && totalSec > 0 ? Math.floor(totalSec) : 0
  const h = Math.floor(safe / 3600)
  const m = Math.floor((safe % 3600) / 60)
  const s = String(safe % 60).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`
}

/** Compact duration for dense table cells — "1h 04m", "4m 12s", "38s". */
export function formatDurationShort(totalSec: number): string {
  const safe = Number.isFinite(totalSec) && totalSec > 0 ? Math.floor(totalSec) : 0
  if (safe >= 3600) {
    return `${Math.floor(safe / 3600)}h ${String(Math.floor((safe % 3600) / 60)).padStart(2, '0')}m`
  }
  if (safe >= 60) return `${Math.floor(safe / 60)}m ${String(safe % 60).padStart(2, '0')}s`
  return `${safe}s`
}

export function formatPercent(value: number): string {
  if (!Number.isFinite(value)) return '0%'
  return `${Math.round(value * 10) / 10}%`
}

export function formatInt(value: number): string {
  if (!Number.isFinite(value)) return '0'
  return value.toLocaleString()
}

/** Bucket label for the pivot column headers — local time, 24h. */
export function formatBucketLabel(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

/**
 * The interval pivot's row order.
 *
 * This IS the KC2 Campaign Summary V2 row list, in its original order, so a
 * supervisor moving between the two systems reads the same table. Two rows are
 * additions KC3 can support and KC2 could not: `serviceLevelPct` (KC2 has no
 * SL target to measure against) and `maxWaitSec`.
 */
export const PIVOT_METRICS: ReadonlyArray<{
  key: keyof SummaryMetrics
  label: string
  format: PivotRow['format']
}> = [
  { key: 'totalCalls', label: 'Total Calls', format: 'int' },
  { key: 'totalInbound', label: 'Total Inbound Calls', format: 'int' },
  { key: 'totalOutbound', label: 'Total Outbound Calls', format: 'int' },
  { key: 'answeredTotal', label: 'Total Answered Calls', format: 'int' },
  { key: 'abandonedTotal', label: 'Total Abandoned Calls', format: 'int' },
  { key: 'abandonedIvr', label: 'Abandoned in IVR', format: 'int' },
  { key: 'abandonedQueue', label: 'Abandoned in Queue', format: 'int' },
  { key: 'totalTalkSec', label: 'Total Talk Time', format: 'duration' },
  { key: 'avgTalkSec', label: 'Avg. Talk Time', format: 'duration' },
  { key: 'abandonRatePct', label: 'Avg. Abandoned', format: 'percent' },
  { key: 'ivrAbandonRatePct', label: 'Avg. Abandoned in IVR', format: 'percent' },
  { key: 'avgHandleSec', label: 'Avg. Handle Time (AHT)', format: 'duration' },
  { key: 'totalHoldSec', label: 'Total Hold Time', format: 'duration' },
  { key: 'avgHoldSec', label: 'Average Hold Time', format: 'duration' },
  { key: 'totalWaitSec', label: 'Total Wait Time', format: 'duration' },
  { key: 'avgAnswerSpeedSec', label: 'ASA (Avg Speed of Answer)', format: 'duration' },
  { key: 'maxWaitSec', label: 'Longest Wait', format: 'duration' },
  { key: 'serviceLevelPct', label: 'Service Level', format: 'percent' },
]

/** Render one pivot cell according to its row's declared format. */
export function formatMetric(value: number, format: PivotRow['format']): string {
  if (format === 'duration') return formatDuration(value)
  if (format === 'percent') return formatPercent(value)
  return formatInt(value)
}

/**
 * Transpose interval rows into metric-per-row / bucket-per-column.
 *
 * The API returns row-per-bucket because that is what SQL produces and what
 * any other consumer would want; the transpose is purely how this dashboard
 * chooses to read it (and how the KC2 reference renders it), so it lives in
 * the UI rather than the wire format.
 */
export function buildPivot(intervals: IntervalRow[]): PivotRow[] {
  return PIVOT_METRICS.map((metric) => {
    const values: Record<string, number> = {}
    for (const row of intervals) {
      values[row.bucketStart] = Number(row[metric.key] ?? 0)
    }
    return { ...metric, values }
  })
}

/**
 * Colour band for a service-level figure. Returned as a semantic name so the
 * caller picks the actual colours — the same bands drive text, chips and
 * borders, which need different shades of the same judgement.
 */
export function slBand(pct: number): 'good' | 'warn' | 'bad' {
  if (pct >= 80) return 'good'
  if (pct >= 60) return 'warn'
  return 'bad'
}

/** Abandon rate is inverted — low is good, and 3% is the Ofcom ceiling. */
export function abandonBand(pct: number): 'good' | 'warn' | 'bad' {
  if (pct <= 3) return 'good'
  if (pct <= 8) return 'warn'
  return 'bad'
}
